import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";

type TooltipSide = "top" | "bottom" | "left" | "right";

type TooltipAlign = "start" | "center" | "end";

type TooltipProps = {
  content: ReactNode;
  children: ReactNode;
  side?: TooltipSide;
  align?: TooltipAlign;
  delay?: number;
  className?: string;
  /** Inline style forwarded to the trigger wrapper (e.g. a flex width). */
  style?: CSSProperties;
  panelClassName?: string;
  disabled?: boolean;
  /**
   * Override the width cap (maps to `max-w-{value}`). The panel defaults to
   * `max-w-xs` and wraps, so a long hint never stretches across the page; pass a
   * larger size for content that genuinely needs more room. Ignored when a
   * consumer supplies its own `panelClassName` (that consumer owns its layout).
   */
  maxWidth?: TooltipMaxWidth;
};

const alignClasses: Record<TooltipAlign, string> = {
  start: "items-start",
  center: "items-center",
  end: "items-end"
};

export type TooltipMaxWidth = "xs" | "sm" | "md" | "lg" | "xl" | "64" | "80" | "96";

const maxWidthClasses: Record<TooltipMaxWidth, string> = {
  xs: "max-w-xs",
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "64": "max-w-64",
  "80": "max-w-80",
  "96": "max-w-96",
};

/**
 * Whether tooltip content is effectively empty: `null`, `undefined`, `false`, or
 * a string that is blank once trimmed. Empty content must never open a panel or
 * set `aria-describedby`, so a hover cannot flash a bordered box with nothing in
 * it and no empty description reaches assistive tech. Pure, so the guard is
 * unit-testable without rendering the hover-only panel.
 */
export function isEmptyTooltipContent(content: ReactNode): boolean {
  return (
    content == null ||
    content === false ||
    (typeof content === "string" && content.trim() === "")
  );
}

/**
 * The tooltip panel's class list, given the width overrides. Pure so the
 * width policy is unit-testable without rendering the hover-only panel.
 *
 * Policy (the Founder's "tooltips have a maximum width" rule): a tooltip that
 * does not supply its own `panelClassName` caps at `max-w-xs` and wraps, so a
 * long hint no longer stretches ~900px across the page; `w-max` keeps a SHORT
 * hint at its natural one-line width, and only content past the cap wraps. A
 * consumer that passes `panelClassName` owns its panel layout, so no cap/wrap is
 * injected (its class list stays byte-identical) — an explicit `maxWidth` prop
 * still applies. `cn` (tailwind-merge) lets a `panelClassName` width win.
 */
export function tooltipPanelClass(opts: {
  maxWidth?: TooltipMaxWidth;
  panelClassName?: string;
}): string {
  const ownsPanelLayout = Boolean(opts.panelClassName);
  const resolvedMaxWidth = opts.maxWidth
    ? maxWidthClasses[opts.maxWidth]
    : ownsPanelLayout
      ? undefined
      : "max-w-xs";
  return cn(
    "pointer-events-none z-[9999] rounded-md border border-border/60 bg-card/95 px-2 py-1 text-xs text-foreground shadow-soft",
    // break-words lets an unbreakable token (e.g. a no-space JSON/log line) wrap
    // inside the max-width box instead of overflowing it; normal whitespace-
    // wrapping content is unaffected.
    "break-words",
    "transition-opacity duration-150",
    // Cap + wrap for every tooltip that does not own its panel layout.
    !ownsPanelLayout && "w-max whitespace-normal text-left leading-snug",
    resolvedMaxWidth,
    opts.panelClassName,
  );
}

// ── Global Tooltip Coordination ──────────────────────────────────────────────
// Module-level singleton ensures only one tooltip is visible at a time across
// the entire app. No provider needed — works for portals, modals, everything.

/** Dismiss function of the currently-visible tooltip (null when none open). */
let globalDismiss: (() => void) | null = null;

/** Timestamp (ms) of the last tooltip close. Used for skip-delay on rapid hover. */
let lastCloseTime = 0;

/**
 * Duration (ms) after a tooltip closes during which the NEXT tooltip opens
 * instantly (no hover delay). Creates smooth scanning across adjacent triggers.
 */
const SKIP_DELAY_DURATION = 300;

export const Tooltip = ({
  content,
  children,
  side = "top",
  align = "center",
  delay = 150,
  className,
  style,
  panelClassName,
  disabled = false,
  maxWidth,
}: TooltipProps) => {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const tooltipId = useId();
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const panelRef = useRef<HTMLSpanElement | null>(null);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  // Empty content is suppressed exactly like `disabled`: no panel, no describedby.
  const suppressed = disabled || isEmptyTooltipContent(content);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    clearTimer();
    setOpen(false);
    if (globalDismiss === dismiss) {
      globalDismiss = null;
      lastCloseTime = Date.now();
    }
  }, [clearTimer]);

  const handleOpen = useCallback(() => {
    if (suppressed) return;
    clearTimer();

    // Dismiss any other open tooltip synchronously — prevents two-visible state
    if (globalDismiss && globalDismiss !== dismiss) {
      globalDismiss();
    }

    // If a tooltip just closed, skip the delay for instant scanning feel
    const elapsed = Date.now() - lastCloseTime;
    const effectiveDelay = elapsed < SKIP_DELAY_DURATION ? 0 : delay;

    if (effectiveDelay === 0) {
      setOpen(true);
      globalDismiss = dismiss;
    } else {
      timeoutRef.current = window.setTimeout(() => {
        setOpen(true);
        globalDismiss = dismiss;
      }, effectiveDelay);
    }
  }, [suppressed, clearTimer, dismiss, delay]);

  const handleClose = useCallback(() => {
    dismiss();
  }, [dismiss]);

  // Cleanup on unmount: clear pending timeout and release global ownership
  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (globalDismiss === dismiss) {
        globalDismiss = null;
        lastCloseTime = Date.now();
      }
    };
  }, [dismiss]);

  useLayoutEffect(() => {
    if (!open) return;

    const calculatePosition = () => {
      const triggerEl = triggerRef.current;
      const panelEl = panelRef.current;
      if (!triggerEl || !panelEl) return;

      const triggerRect = triggerEl.getBoundingClientRect();
      const panelRect = panelEl.getBoundingClientRect();
      const offset = 8;
      const margin = 8;

      let top = triggerRect.top - offset - panelRect.height;
      let left = triggerRect.left + triggerRect.width / 2 - panelRect.width / 2;

      if (side === "top") {
        top = triggerRect.top - offset - panelRect.height;
      } else if (side === "bottom") {
        top = triggerRect.bottom + offset;
      } else if (side === "left") {
        left = triggerRect.left - offset - panelRect.width;
        top = triggerRect.top + triggerRect.height / 2 - panelRect.height / 2;
      } else if (side === "right") {
        left = triggerRect.right + offset;
        top = triggerRect.top + triggerRect.height / 2 - panelRect.height / 2;
      }

      if (side === "top" || side === "bottom") {
        if (align === "start") left = triggerRect.left;
        if (align === "end") left = triggerRect.right - panelRect.width;
      } else {
        if (align === "start") top = triggerRect.top;
        if (align === "end") top = triggerRect.bottom - panelRect.height;
      }

      const clampedTop = Math.max(margin, Math.min(top, window.innerHeight - panelRect.height - margin));
      const clampedLeft = Math.max(margin, Math.min(left, window.innerWidth - panelRect.width - margin));

      setPosition({ top: clampedTop, left: clampedLeft });
    };

    calculatePosition();
    window.addEventListener("resize", calculatePosition);
    window.addEventListener("scroll", calculatePosition, true);
    return () => {
      window.removeEventListener("resize", calculatePosition);
      window.removeEventListener("scroll", calculatePosition, true);
    };
  }, [open, side, align]);

  return (
    <span
      ref={triggerRef}
      className={cn("relative inline-flex", alignClasses[align], className)}
      style={style}
      onMouseEnter={handleOpen}
      onMouseLeave={handleClose}
      onFocusCapture={handleOpen}
      onBlurCapture={handleClose}
      aria-describedby={open && !suppressed ? tooltipId : undefined}
    >
      {children}
      {open && !suppressed &&
        createPortal(
          <span
            id={tooltipId}
            ref={panelRef}
            role="tooltip"
            style={{ position: "fixed", top: position.top, left: position.left }}
            className={tooltipPanelClass({ maxWidth, panelClassName })}
          >
            {content}
          </span>,
          document.body
        )}
    </span>
  );
};
